import {Serie} from '@nivo/line'
import NumberHelper from "../../../shared/helper/NumberHelper";
import {
	AggregationServerState,
	Statistic as AdminStatistic,
	Webservice
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "../../../shared/helper/FunctionHelper";

export class Statistic {
	private statisticData: AdminStatistic;
	private startDate: Date;
	private endDate: Date;

	constructor(data: AdminStatistic) {
		this.statisticData = data;
		this.startDate = new Date();
		this.endDate = new Date();
	}

	public getFormatAverage(webservice: Webservice): Array<any> {
		if (!FunctionHelper.exists(this.statisticData.data![webservice])) {
			return [];
		}

		let executionTime: Map<string, number> = new Map<string, number>();
		let jobs: Map<string, number> = new Map<string, number>();

		for (let entry of Object.values(this.statisticData.data![webservice])) {
			for (let [format, formatEntry] of Object.entries(entry.formats!)) {
				if (executionTime.has(format)) {
					jobs.set(format, FunctionHelper.applyValue(
						jobs.get(format), 0
					) + formatEntry.numberOfJobs!);
					executionTime.set(format, FunctionHelper.applyValue(
						executionTime.get(format), 0
					) + formatEntry.executionTimeMs!);
				} else {
					jobs.set(format, formatEntry.numberOfJobs!);
					executionTime.set(format, formatEntry.executionTimeMs!);
				}
			}
		}

		let result: Array<any> = [];
		for (let [format, value] of jobs.entries()) {
			result.push({
				id: format,
				jobs: value,
				value: NumberHelper.format(FunctionHelper.applyValue(
					executionTime.get(format), 0
				) / value / 1000, 2) // in seconds
			});
		}

		return result;
	}

	public getFormatJobs(webservice: Webservice): Array<any> {
		if (!FunctionHelper.exists(this.statisticData.data![webservice])) {
			return [];
		}

		let executionTime: Map<string, number> = new Map<string, number>();
		let jobs: Map<string, number> = new Map<string, number>();

		for (let entry of Object.values(this.statisticData.data![webservice])) {
			for (let [format, formatEntry] of Object.entries(entry.formats!)) {
				if (executionTime.has(format)) {
					jobs.set(format, FunctionHelper.applyValue(
						jobs.get(format), 0
					) + formatEntry.numberOfJobs!);
					executionTime.set(format, FunctionHelper.applyValue(
						executionTime.get(format), 0
					) + formatEntry.executionTimeMs!);
				} else {
					jobs.set(format, formatEntry.numberOfJobs!);
					executionTime.set(format, formatEntry.executionTimeMs!);
				}
			}
		}

		let result: Array<any> = [];
		for (let [format, value] of jobs.entries()) {
			result.push({
				id: format,
				value: value,
				average: NumberHelper.format(FunctionHelper.applyValue(
					executionTime.get(format), 0
				) / value / 1000, 2) // in seconds
			});
		}

		return result;
	}

	private generateEmptyValues(aggregation: AggregationServerState, additionalFields?: any): Map<string, any> {
		let result: Map<string, any> = new Map<string, any>();
		let tempStartDate: Date = new Date(this.startDate);
		let tempEndDate: Date = new Date(this.endDate);
		tempStartDate.setSeconds(0, 0);

		while (tempStartDate <= tempEndDate) {
			switch (aggregation) {
				case AggregationServerState.Minute:
					tempStartDate.setMinutes(tempStartDate.getMinutes() + 1);
					result.set(tempStartDate.toISOString(), {y: 0, ...additionalFields});
					break;
				case AggregationServerState.Hour:
					tempStartDate.setHours(tempStartDate.getHours() + 1, 0);
					result.set(tempStartDate.toISOString(), {y: 0, ...additionalFields});
					break;
				case AggregationServerState.Day:
					tempStartDate.setHours(0, 0);
					tempStartDate.setDate(tempStartDate.getDate() + 1);
					result.set(tempStartDate.toISOString(), {y: 0, ...additionalFields});
					break;
			}
		}

		return result;
	}

	public getAverage(aggregation: AggregationServerState): Array<Serie> {
		let result: Array<Serie> = [];

		for (let [webservice, webserviceData] of Object.entries(this.statisticData.data!)) {
			let serieData: Map<string, any> = this.generateEmptyValues(aggregation, {jobs: 0});

			for (let [timestamp, entry] of Object.entries(webserviceData)) {
				let key: Date = new Date(parseInt(timestamp));
				key.setSeconds(0, 0);

				switch (aggregation) {
					case AggregationServerState.Hour:
						key.setMinutes(0);
						break;
					case AggregationServerState.Day:
						key.setHours(0, 0);
						break;
				}

				if (serieData.has(key.toISOString())) {
					serieData.set(
						key.toISOString(),
						{
							// in seconds
							y: NumberHelper.format(entry.executionTimeMs! / entry.numberOfJobs! / 1000, 2),
							jobs: entry.numberOfJobs!
						}
					);
				}
			}

			let data: Array<{ x: string, y: any, jobs: number }> = [];
			for (let [key, values] of serieData.entries()) {
				data.push({x: key, ...values});
			}

			result.push({
				id: webservice,
				data: data
			});
		}

		return result;
	}

	public getJobs(aggregation: AggregationServerState): Array<Serie> {
		let result: Array<Serie> = [];

		for (let [webservice, webserviceData] of Object.entries(this.statisticData.data!)) {
			let serieData: Map<string, any> = this.generateEmptyValues(aggregation, {average: 0});

			for (let [timestamp, entry] of Object.entries(webserviceData)) {
				let key: Date = new Date(parseInt(timestamp));
				key.setSeconds(0, 0);

				switch (aggregation) {
					case AggregationServerState.Hour:
						key.setMinutes(0);
						break;
					case AggregationServerState.Day:
						key.setHours(0, 0);
						break;
				}

				if (serieData.has(key.toISOString())) {
					serieData.set(key.toISOString(), {
						y: entry.numberOfJobs!,
						// in seconds
						average: NumberHelper.format(entry.executionTimeMs! / entry.numberOfJobs! / 1000, 2)
					});
				}
			}

			let data: Array<{ x: string, y: any, average: number }> = [];
			for (let [key, values] of serieData.entries()) {
				data.push({x: key, ...values});
			}

			result.push({
				id: webservice,
				data: data
			});
		}

		return result;
	}

	public getQueueUsage(aggregation: AggregationServerState): Array<Serie> {
		let result: Array<Serie> = [];

		for (let [webservice, webserviceData] of Object.entries(this.statisticData.data!)) {
			let serieData: Map<string, any> = this.generateEmptyValues(aggregation);

			for (let [timestamp, entry] of Object.entries(webserviceData)) {
				let key: Date = new Date(parseInt(timestamp));
				key.setSeconds(0, 0);

				switch (aggregation) {
					case AggregationServerState.Hour:
						key.setMinutes(0);
						break;
					case AggregationServerState.Day:
						key.setHours(0, 0);
						break;
				}

				if (serieData.has(key.toISOString())) {
					serieData.set(
						key.toISOString(),
						{y: NumberHelper.format(entry.instances!.queueSize! / entry.instances!.count!, 2)}
					);
				}
			}

			let data: Array<{ x: string, y: number }> = [];
			for (let [key, values] of serieData.entries()) {
				data.push({x: key, ...values});
			}

			result.push({
				id: webservice,
				data: data
			});
		}

		return result;
	}

	public getPoolUsage(aggregation: AggregationServerState): Array<Serie> {
		let result: Array<Serie> = [];

		for (let [webservice, webserviceData] of Object.entries(this.statisticData.data!)) {
			let serieData: Map<string, any> = this.generateEmptyValues(aggregation);

			for (let [timestamp, entry] of Object.entries(webserviceData)) {
				let key: Date = new Date(parseInt(timestamp));
				key.setSeconds(0, 0);

				switch (aggregation) {
					case AggregationServerState.Hour:
						key.setMinutes(0);
						break;
					case AggregationServerState.Day:
						key.setHours(0, 0);
						break;
				}

				if (serieData.has(key.toISOString())) {
					serieData.set(
						key.toISOString(),
						{y: NumberHelper.format(entry.instances!.poolSize! / entry.instances!.count!, 2)}
					);
				}
			}

			let data: Array<{ x: string, y: number }> = [];
			for (let [key, values] of serieData.entries()) {
				data.push({x: key, ...values});
			}

			result.push({
				id: webservice,
				data: data
			});
		}

		return result;
	}

	public getErrors(aggregation: AggregationServerState): Array<Serie> {
		let result: Array<any> = [];

		for (let [webservice, webserviceData] of Object.entries(this.statisticData.data!)) {
			let serieData: Map<string, any> = this.generateEmptyValues(aggregation);

			for (let [timestamp, entry] of Object.entries(webserviceData)) {
				let key: Date = new Date(parseInt(timestamp));
				key.setSeconds(0, 0);

				switch (aggregation) {
					case AggregationServerState.Hour:
						key.setMinutes(0);
						break;
					case AggregationServerState.Day:
						key.setHours(0, 0);
						break;
				}

				if (serieData.has(key.toISOString())) {
					serieData.set(key.toISOString(), {y: entry.numberOfErrors!});
				}
			}

			let data: Array<{ x: string, y: number }> = [];
			for (let [key, values] of serieData.entries()) {
				data.push({x: key, ...values});
			}

			result.push({
				id: webservice,
				data: data
			});
		}

		return result;
	}

	public setStart(value: Date) {
		this.startDate = new Date(value);
	}

	public setEnd(value: Date) {
		this.endDate = new Date(value);
	}
}